import { DataImporterType, EmissionSourceType, ReportType } from 'es/app/__generated__/global';
import { ProcessType } from 'es/app/business-logic/domain-models/Process';
import Site from 'es/app/business-logic/domain-models/Site';
import { ProcessVariableType } from 'es/app/business-logic/services/process/fetchProcessVariablesForProcess';
import { ProcessSchemaType } from 'es/app/business-logic/services/processSchemas/fetchProcessSchemasForProcess';
import Guid from 'es/core/types/Guid';
import { useLocation } from 'react-router-dom';
import urlJoin from 'url-join';
import { SequenceType } from '../../business-logic/domain-models/Sequences';
import { CalmetConfigurationType } from 'es/app/views/site/models/model.constants';

const SITE_REGEX = new RegExp('^/company/([^/]+)(?:/site/([^/]+))*', 'i');

export const isSiteRoute = (route: Location | string) => {
    const pathname = route instanceof Location ? route.pathname : route;
    return pathname.startsWith('/company');
};

export const useSiteFromUrl = (): Site => {
    const { pathname } = useLocation();
    return getSiteFromUrl(pathname);
};

export const getSiteFromUrl = (pathname: string) => {
    const match = pathname.match(SITE_REGEX);
    if (!match) return Site.Global;

    const [, companyAlias, facilityAlias] = match;

    return companyAlias && facilityAlias ? Site.get(companyAlias, facilityAlias) : Site.Global;
};

export const createSiteRouteMap = (companyAlias = ':companyAlias', facilityAlias = ':facilityAlias') => {
    const r = (route = '') => urlJoin('/company', companyAlias, 'site', facilityAlias, route);

    return {
        alerts: {
            search: () => r('/alerts'),
            create: () => r('/alerts/new'),
            edit: (alertId?: Guid) => {
                const root = r(`/alerts/edit/${alertId ?? ':id'}`);
                return {
                    root,
                    recipients: `${root}/recipients`,
                    history: `${root}/history`,
                    noise: `${root}/noise`,
                    correlation: `${root}/correlation`,
                    dataViewer: `${root}/data-viewer`,
                };
            },
        },
        dataImporters: {
            search: () => r('/data-importers'),
            create: (type?: DataImporterType) => r(`/data-importers/${type?.toString() ?? ':type'}/new`),
            edit: (dataImportId?: Guid) => {
                const root = r(`/data-importers/edit/${dataImportId ?? ':id'}`);
                return {
                    root,
                    logs: `${root}/logs`,
                };
            },
        },
        emissionSources: {
            search: () => r('/emission-sources'),
            create: (type?: EmissionSourceType) => r(`/emission-sources/${type?.toString() ?? ':type'}/new`),
            edit: (emissionSourceId?: Guid) => {
                const root = r(`/emission-sources/${emissionSourceId ?? ':id'}`);
                return {
                    root,
                };
            },
        },
        models: {
            search: () => r('/models'),
            calpuff: () => {
                const rootPath = r(`/models/calpuff`);
                return {
                    create: () => `${rootPath}/new`,
                    edit: (calpuffConfigurationId?: Guid | null) => {
                        const rootEdit = `${rootPath}/${calpuffConfigurationId ?? ':id'}`;
                        return {
                            root: rootEdit,
                            run: `${rootEdit}/run`,
                        };
                    },
                };
            },
            calmet: () => {
                const rootPath = r(`/models/calmet`);
                return {
                    create: (type?: CalmetConfigurationType) => `${rootPath}/new/${type ?? ':type'}`,
                    edit: (calmetConfigurationId?: Guid | null) => {
                        const rootEdit = `${rootPath}/${calmetConfigurationId ?? ':id'}`;
                        return {
                            root: rootEdit,
                            run: `${rootEdit}/run`,
                        };
                    },
                };
            },
            trajectory: () => {
                const rootPath = r(`/models/trajectory`);
                return {
                    create: () => `${rootPath}/new`,
                    edit: (trajectoryConfigurationId?: Guid | null) => {
                        const rootEdit = `${rootPath}/${trajectoryConfigurationId ?? ':id'}`;
                        return {
                            root: rootEdit,
                            run: `${rootEdit}/run`,
                        };
                    },
                };
            },
        },
        processes: {
            root: r('/processes'),
            create: (type?: ProcessType) => r(`/processes/${type?.toString()?.toLowerCase() ?? ':type'}/new`),
            edit: (processId?: Guid) => {
                return r(`/processes/${processId ?? ':id'}`);
            },
            clone: (processId?: Guid) => {
                return r(`/processes/${processId ?? ':id'}/clone`);
            },
            variables: (processId?: Guid) => {
                const rootPath = `/processes/${processId ?? ':id'}/variables`;
                return {
                    root: r(rootPath),
                    create: (type?: ProcessVariableType) => {
                        return r(`${rootPath}/${type?.toString() ?? ':type'}/new`);
                    },
                    edit: (processVariableId?: Guid | null) => {
                        const rootEdit = r(`${rootPath}/${processVariableId ?? ':variableId'}`);
                        return {
                            root: rootEdit,
                            dataViewer: `${rootEdit}/data-viewer`,
                        };
                    },
                };
            },
            processSchemas: (processId?: Guid, tab?: 'processSchemas') => {
                const tabPath = (tab && `?${tab}`) || '';
                const rootPath = `/processes/${processId ?? ':id'}/process-schemas`;
                return {
                    root: r(rootPath),
                    search: () => {
                        return r(`${rootPath}${tabPath}`);
                    },
                    create: (type?: ProcessSchemaType) => {
                        return r(`${rootPath}/${type?.toString() ?? ':type'}/new${tabPath}`);
                    },
                    edit: (processSchemaId?: Guid | null) => {
                        const rootEdit = r(`${rootPath}/${processSchemaId ?? ':processSchemaId'}`);
                        return {
                            root: rootEdit,
                            noiseClassificationHistory: `${rootEdit}/noise-classification-history`,
                        };
                    },
                };
            },
            api: (processId?: Guid, tab?: 'api') => {
                const tabPath = (tab && `?${tab}`) || '';
                const rootPath = `/processes/${processId ?? ':id'}/api`;
                return {
                    root: r(rootPath),
                    search: () => {
                        return r(`${rootPath}${tabPath}`);
                    },
                    create: () => {
                        return r(`${rootPath}/new${tabPath}`);
                    },
                    edit: (apiCredentialId?: Guid) => {
                        return r(`${rootPath}/edit/${apiCredentialId ?? ':credentialId'}${tabPath}`);
                    },
                    transfer: (apiCredentialId?: Guid) => {
                        return r(`${rootPath}/transfer/${apiCredentialId ?? ':credentialId'}${tabPath}`);
                    },
                };
            },
            locationHistory: (processId?: Guid) => {
                const rootPath = `/processes/${processId ?? ':id'}/location-history`;
                return {
                    root: r(rootPath),
                    create: () => {
                        return {
                            root: r(`${rootPath}/new`),
                        };
                    },
                    edit: (monitorLocationId?: string) => {
                        return {
                            root: r(`${rootPath}/edit/${monitorLocationId ?? ':monitorLocationId'}`),
                        };
                    },
                };
            },
            arena: (processId?: Guid) => {
                return {
                    root: r(`/processes/${processId ?? ':id'}/arena`),
                };
            },
            dataViewer: (processId?: Guid) => {
                return {
                    root: r(`/processes/${processId ?? ':id'}/dataViewer`),
                };
            },
            deletion: (processId?: Guid) => {
                return {
                    root: r(`/processes/${processId ?? ':id'}/deletion`),
                };
            },
        },
        reports: {
            search: () => r('/reports'),
            create: (type?: ReportType) => r(`/reports/${type?.toString() ?? ':type'}/new`),
            edit: (reportId?: Guid) => {
                const root = r(`/reports/edit/${reportId ?? ':id'}`);
                const schedulesRoot = `${root}/schedules`;
                return {
                    root,
                    variables: `${root}/variables`,
                    alerts: `${root}/alerts`,
                    schedules: {
                        root: schedulesRoot,
                        create: `${schedulesRoot}/new`,
                        edit: (scheduleId?: Guid) => {
                            const schedulesEditRoot = `${schedulesRoot}/${scheduleId ?? ':scheduleId'}`;
                            return {
                                root: schedulesEditRoot,
                                executionLog: `${schedulesEditRoot}/execution-log`,
                            };
                        },
                    },
                    customisation: `${root}/customisation`,
                    history: `${root}/history`,
                };
            },
        },
        sequences: {
            search: () => r('/sequences'),
            create: (type?: SequenceType) => r(`/sequences/${type?.toString()?.toLowerCase() ?? ':type'}/new`),
            edit: (sequenceId?: Guid) => {
                const root = r(`/sequences/edit/${sequenceId ?? ':id'}`);
                return {
                    root,
                    executionLogs: `${root}/execution-logs`,
                };
            },
        },
        files: {
            search: () => r('/files'),
            searchDeep: () => r('/files/*'),
        },
        temporalThresholds: {
            search: () => r('/temporal-thresholds'),
            create: () => {
                const root = r('/temporal-thresholds/new');
                return {
                    root,
                    recurringThresholds: `${root}/recurring-thresholds`,
                    specialThresholds: `${root}/special-thresholds`,
                };
            },
            edit: (temporalThresholdId?: Guid) => {
                const root = r(`/temporal-thresholds/${temporalThresholdId ?? ':id'}`);
                return {
                    root,
                    recurringThresholds: `${root}/recurring-thresholds`,
                    specialThresholds: `${root}/special-thresholds`,
                };
            },
        },
    };
};

export const SiteRouteMap = createSiteRouteMap();
